import { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ResizableImage from './ResizableImageExtension';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import EditorToolbar from './EditorToolbar';
import api from '../utils/api';

async function uploadImage(file, uploadPath) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await api.post(uploadPath, fd);
  return r.data.url;
}

export default function RichEditor({
  content = '',
  onChange,
  editable = true,
  placeholder = '내용을 입력하세요...',
  uploadPath = '/posts/upload',
}) {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      ResizableImage,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder }),
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      handleDrop: (view, event) => {
        const files = Array.from(event.dataTransfer?.files || []).filter(f => f.type.startsWith('image/'));
        if (!files.length) return false;
        event.preventDefault();
        files.forEach(async (f) => {
          const url = await uploadImage(f, uploadPath);
          const node = view.state.schema.nodes.image.create({ src: url });
          view.dispatch(view.state.tr.replaceSelectionWith(node));
        });
        return true;
      },
      handlePaste: (view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const imageItem = items.find(i => i.type.startsWith('image/'));
        if (!imageItem) return false;
        event.preventDefault();
        const file = imageItem.getAsFile();
        if (!file) return false;
        (async () => {
          const url = await uploadImage(file, uploadPath);
          const node = view.state.schema.nodes.image.create({ src: url });
          view.dispatch(view.state.tr.replaceSelectionWith(node));
        })();
        return true;
      },
    },
  });

  // content prop 변경 시 에디터 동기화 (초기 로드 후 데이터 도착 시)
  useEffect(() => {
    if (!editor || !content) return;
    const current = editor.getHTML();
    if (current === content) return;
    editor.commands.setContent(content, false);
  }, [content]);

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editable, editor]);

  const handleImageButton = () => fileInputRef.current?.click();
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
    if (!files.length || !editor) return;
    for (const f of files) {
      const url = await uploadImage(f, uploadPath);
      editor.chain().focus().setImage({ src: url }).run();
    }
    e.target.value = '';
  };

  return (
    <div className="flex flex-col">
      {editable && <EditorToolbar editor={editor} onImageUpload={handleImageButton} />}
      <EditorContent editor={editor} />
      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
    </div>
  );
}
